import { Field, useFormikContext } from "formik";
import { useParcels } from "@/context/Parcels.context";
import {
  CommonFormLayout,
  FormRow,
  Input,
  DetailedRadioButton,
  SearchInput,
  Error,
  Button,
  Textarea,
  ShowWhen,
  NumberStepper,
  UploadButton,
} from "@/components/common";
import {
  PlusSmallIcon,
  TrashIcon,
  UploadIcon,
} from "@/components/assets/icons";
import { useState } from "react";
import ProductDetails from "../../../product-details/product-details";
import classNames from "classnames";
import { useGetShipmentTypesRequest } from "@/api/useGetShipmentTypesRequest";
import { Category } from "../../../get-parcels-table/components";
import { ProductGE, TParcelItemsGE } from "@/types/parcel.types";
import { useTranslations } from "next-intl";
import ParcelItemModalGE from "./parcel-items-modal-ge";
import { ButtonColor, ModalType } from "@/enums";
import { useDeleteShipmentInvoicePhotoRequest } from "@/api/useDeleteShipmentInvoicePhotoRequest";
import { openInvoiceFile } from "@/utils/downloadInvoice";
import { useGetCalculateShipmentRequest } from "@/api/useGetCalculateShipomentRequest";
import { toast } from "react-toastify";

export const getTotalPrice = (products: ProductGE[]) => {
  return parseFloat(
    products
      ?.reduce((acc, product: any) => {
        return acc + parseFloat(product?.productPriceUsd || "0");
      }, 0)
      .toFixed(2),
  );
};

const ParcelItemsGE = () => {
  const t = useTranslations();

  const { activeStep, setActiveStep, parcelId } = useParcels();
  const [openModal, setOpenModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [modalType, setModalType] = useState<ModalType | null>(null);

  const {
    values,
    setFieldValue,
    validateForm,
    errors,
    setErrors,
    getFieldMeta,
    setFieldError,
    setFieldTouched,
  } = useFormikContext<TParcelItemsGE>();

  const {
    products,
    productCategoryId,
    productPriceUsd,
    productAmount,
    productDescription,
    invoiceFile,
  } = values;

  const { data: productCategories } = useGetShipmentTypesRequest();
  const productCategoriesData = productCategories?.data?.data || [];

  const { data: calculateShipmentPrice } = useGetCalculateShipmentRequest();
  const shipmenPrices = calculateShipmentPrice?.data?.data;

  const handleUploadInvoice = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = () => {
      const base64 = (reader.result as string).split(",")[1];
      const fileExtension = file.name.split(".").pop() ?? "";

      const invoiceFile = {
        fileExtension,
        fileContentInBase64: base64,
      };
      if (invoiceFile) {
        setFieldValue("invoiceFile", invoiceFile);
        setErrors({});
      }
    };

    reader.readAsDataURL(file);
  };

  const deleteInvoice = useDeleteShipmentInvoicePhotoRequest();

  const handleDeleteInvoice = () =>
    parcelId
      ? deleteInvoice({ shipmentId: parcelId }).then((res) => {
          res.data?.data?.completed && setFieldValue("invoiceFile", undefined);
        })
      : setFieldValue("invoiceFile", undefined);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const deleteItem = (itemId: number) => {
    setFieldValue(
      "products",
      products?.filter((_, i) => i !== itemId),
    );
  };

  const handleOpenEditModal = (product: ProductGE, index: number) => {
    setModalType(ModalType.EDIT);
    setOpenModal(true);
    setFieldValue("index", index);
    setFieldValue("productCategoryId", product?.productCategoryId);
    setFieldValue("productPriceUsd", product?.productPriceUsd);
    setFieldValue("productAmount", product?.productAmount);
    setFieldValue("productDescription", product?.productDescription);
  };

  const handleMainButtonClick = async () => {
    if (activeStep === 2) {
      const productsTotalPrice = getTotalPrice(products);
      if (
        productsTotalPrice * shipmenPrices?.currentRateNationalBank >
          shipmenPrices?.customPriceLimitNational &&
        !values.invoiceFile?.fileContentInBase64
      ) {
        setErrors({
          invoiceFile: {
            fileExtension: "invoice_file_required",
          },
        });
        toast.error(t("invoice_file_required"), {
          position: "top-center",
        });
        return;
        // validationStepStatusRef.current.invoiceFile = false;
      }
    }

    setIsSubmitted(true);
    try {
      if (products?.length > 0) {
        if (Object.keys(errors).length === 0) {
          setErrors({});
          // submitForm();
          setActiveStep(activeStep + 1);
        } else {
          console.error(errors);
        }
      } else {
        const validationResponse = await validateForm();
        if (validationResponse) {
          if (Object.keys(validationResponse).length === 0) {
            if (productCategoryId && productPriceUsd && productAmount) {
              products
                ? setFieldValue("products", [
                    ...products,
                    {
                      productCategoryId: productCategoryId,
                      productPriceUsd: productPriceUsd,
                      productAmount: productAmount,
                      productDescription: productDescription,
                    },
                  ])
                : setFieldValue("products", [
                    {
                      productCategoryId: productCategoryId,
                      productPriceUsd: productPriceUsd,
                      productAmount: productAmount,
                      productDescription: productDescription,
                    },
                  ]);

              setErrors({});
            } else {
              console.error("Required fields are missing");
            }
          } else {
            console.error(validationResponse);
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <CommonFormLayout
      title={parcelId ? t("update_parcel") : t("add_new_parcel")}
      mainButton={products?.length > 0 ? t("add_parcel") : t("add")}
      secondaryButtonText={products?.length > 0 ? t("back") : t("cancel")}
      secondaryOnClick={() => setActiveStep(activeStep - 1)}
      noSubmit
      mainButtonOnClick={handleMainButtonClick}
    >
      <ShowWhen
        condition={products?.length > 0}
        elseRender={
          <div className="w-[420px]">
            <FormRow className="w-full" rowLabel="product_details">
              <div className={classNames("flex w-full gap-[15px]")}>
                <Field name={`productPriceUsd`}>
                  {({ field, meta }) => (
                    <Input
                      label={"product_price"}
                      value={productPriceUsd}
                      error={
                        isSubmitted ? meta.error : meta.touched && meta.error
                      }
                      currency="$"
                      {...field}
                      onChange={(e) => {
                        const inputValue = e.target.value;

                        if (!/^\d*\.?\d*$/.test(inputValue)) {
                          return;
                        } else {
                          if (inputValue.split(".")[1]?.length > 2) {
                            return;
                          }
                          setFieldValue("productPriceUsd", inputValue);
                        }
                      }}
                    />
                  )}
                </Field>
                <Field name={`productAmount`}>
                  {({ field, meta }) => (
                    <NumberStepper
                      label={"quantity"}
                      value={productAmount}
                      {...field}
                      onNumberChange={(value: string) => {
                        setFieldValue("productAmount", parseFloat(value));
                      }}
                      onChange={(e) => {
                        const inputValue = (e.target as HTMLTextAreaElement)
                          .value;
                        if (/[^0-9]/.test(inputValue)) {
                          return;
                        } else {
                          field.onChange(e);
                        }
                      }}
                      error={
                        isSubmitted ? meta.error : meta.touched && meta.error
                      }
                    />
                  )}
                </Field>
              </div>
            </FormRow>
            <FormRow className="w-full" rowLabel="choose_category">
              <div className="flex flex-col justify-between gap-[10px]">
                <SearchInput
                  label={t("search_category")}
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue((e.target as HTMLTextAreaElement).value);
                  }}
                />
                <div className="flex max-h-[23rem] flex-col justify-between gap-[10px] overflow-y-auto">
                  {productCategoriesData
                    ?.filter((cat) =>
                      cat.shipmentTypeName
                        ?.toLocaleLowerCase()
                        .includes(searchValue?.toLocaleLowerCase()),
                    )
                    ?.map((cat, i) => {
                      return (
                        <DetailedRadioButton
                          key={i}
                          id={i.toString()}
                          name="productCategoryId"
                          label={cat.shipmentTypeName}
                          checked={productCategoryId?.id === cat.shipmentTypeId}
                          onChange={(e) => {
                            (e.target as HTMLInputElement).checked &&
                              setFieldValue("productCategoryId", {
                                id: cat.shipmentTypeId,
                                mainText: cat.shipmentTypeName,
                              });
                          }}
                          icon={<Category category={cat.shipmentTypeId} />}
                          error={
                            isSubmitted
                              ? getFieldMeta("productCategoryId").error
                              : getFieldMeta("productCategoryId").touched &&
                                getFieldMeta("productCategoryId").error
                          }
                          handleTouch={() =>
                            setFieldTouched("productCategoryId")
                          }
                          translation={false}
                        />
                      );
                    })}
                </div>
              </div>
              {errors?.productCategoryId &&
                typeof errors?.productCategoryId === "string" && (
                  <Error
                    text={
                      isSubmitted
                        ? getFieldMeta("productCategoryId").error
                        : getFieldMeta("productCategoryId").touched &&
                          getFieldMeta("productCategoryId").error
                    }
                  />
                )}
            </FormRow>
            <Field name={`productDescription`}>
              {({ field, meta }) => (
                <FormRow className="w-full">
                  <Textarea
                    label="description"
                    id="description"
                    resize="resize-y"
                    className="!rounded-[2.25rem] !px-[2rem] !text-base font-normal"
                    labelClassName="!left-[2rem]"
                    error={
                      isSubmitted ? meta.error : meta.touched && meta.error
                    }
                    {...field}
                    onChange={(e) => {
                      setFieldValue(`productDescription`, e.target.value);
                    }}
                  />
                </FormRow>
              )}
            </Field>
          </div>
        }
      >
        <div className="w-[420px]">
          <FormRow className="w-full" rowLabel="product_details">
            <div className="flex flex-col gap-[20px]">
              {products?.map((product, i) => {
                return (
                  <ProductDetails
                    key={i}
                    productId={i}
                    category={
                      productCategoriesData
                        ?.filter(
                          (cat) =>
                            cat.shipmentTypeId ===
                            product?.productCategoryId?.id,
                        )
                        ?.map((item) => {
                          return {
                            id: item.shipmentTypeId,
                            value: item.shipmentTypeName,
                          };
                        })?.[0]
                    }
                    productPrice={parseFloat(product?.productPriceUsd)
                      ?.toFixed(2)
                      ?.toString()}
                    productQuantity={product?.productAmount?.toString()}
                    onDelete={() => deleteItem(i)}
                    onEdit={() => {
                      handleOpenEditModal(product as ProductGE, i);
                    }}
                  />
                );
              })}
            </div>
            <div className="my-[20px] ml-[23px] flex items-center justify-between">
              <h3 className="text-sm font-medium text-black">
                {t("total_price")}:
              </h3>
              <div className="rounded-[18.5px] bg-[#f9f9f9] px-[24px] py-[10px] text-sm font-semibold text-black">
                ${getTotalPrice(products).toFixed(2)}
              </div>
            </div>
          </FormRow>
          <Button
            variant="outlined-dash"
            onClick={() => {
              setOpenModal(true);
              setModalType(ModalType.ADD);
            }}
          >
            {t("add_product")}&nbsp;
            <PlusSmallIcon />
          </Button>
          <FormRow className="mt-[20px] flex flex-col gap-[10px] rounded-[20px] border-[2px] border-[#f9f9f9] p-[1rem]">
            {invoiceFile?.fileExtension ? (
              <Button
                color={ButtonColor.CUSTOM}
                rightIcon={
                  <TrashIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteInvoice();
                    }}
                  />
                }
                className="h-[62px] bg-[#f9f9f9] pl-[8px] pr-[19px]"
                onClick={() => openInvoiceFile(invoiceFile)}
              >
                <div className="flex items-center gap-[10px] text-[0.8125rem]">
                  <div className="flex h-[46px] w-[46px] items-center justify-center rounded-full bg-white text-base font-semibold">
                    1
                  </div>
                  Invoice
                </div>
              </Button>
            ) : (
              <UploadButton
                rightIcon={<UploadIcon />}
                className="mb-[10px]"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={handleUploadInvoice}
                error={errors?.invoiceFile?.fileExtension}
              >
                {t("upload_invoice_here")}
              </UploadButton>
            )}
          </FormRow>

          {/* // removed description by their suggestion georgian bug #61 */}
          {/* <FormRow>
            <Textarea
              label="description"
              id="description"
              resize="resize-y"
              className="!rounded-[2.25rem] !px-[2rem] !text-base font-normal"
              labelClassName="!left-[2rem]"
              // value={description}
              onChange={(e) => {
                setFieldValue(
                  "description",
                  (e.target as HTMLTextAreaElement).value,
                );
              }}
            />
          </FormRow> */}
          <ParcelItemModalGE
            modalOpen={openModal}
            closeModal={() => {
              setOpenModal(false);
              setModalType(null);
            }}
            modalType={modalType}
          />
        </div>
      </ShowWhen>
    </CommonFormLayout>
  );
};

export default ParcelItemsGE;
